import PropTypes from 'prop-types';
import reverseGeocode from '@rsos/maps/providers/esri/locator/reverseGeocode';

// Number of milliseconds when the local GIS reverse geocoding request should timeout.
const REQUEST_TIMEOUT_MS = 3000;

/**
 * Reverse geocode coordinates using the specified geocode service url. The service returns an
 * address and addressObject if it is found, or an error then fallback if it cannot find a result.
 * @param {Number} latitude - The location's latitude
 * @param {Number} longitude - The location's longitude
 * @param {Function} onReverseGeocoding - Sets if the location is being reverse geocoded
 * @param {String} serviceUrl - A GIS or Esri geocode service url
 * @param {String} token - The GIS authentication token or apiKey
 */
const gisReverseGeocodeLocation = async ({
  latitude,
  longitude,
  onReverseGeocoding,
  serviceUrl,
  token,
}) => {
  if (!serviceUrl) throw new Error('serviceUrl is required.');

  if (onReverseGeocoding) onReverseGeocoding(true);

  try {
    // When the reverse geocode API request is complete, but the Promise fails to resolve or reject,
    // it remains in the pending state. A requestOptions object with the signal property is passed
    // to the function so that it can be manually aborted. More information on this below:
    // https://developers.arcgis.com/javascript/latest/api-reference/esri-rest-locator.html#locationToAddress
    // https://developers.arcgis.com/javascript/latest/api-reference/esri-request.html#RequestOptions
    // https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal#aborting_a_fetch_operation_with_a_timeout
    const requestOptions = { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) };

    const response = await reverseGeocode({
      serviceUrl,
      latitude,
      longitude,
      requestOptions,
      token,
    });

    const addressCandidate = response?.attributes;

    return addressCandidate || null;
  } catch (error) {
    throw new Error();
  } finally {
    if (onReverseGeocoding) onReverseGeocoding(false);
  }
};

gisReverseGeocodeLocation.PropTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  onReverseGeocoding: PropTypes.func,
  serviceUrl: PropTypes.string.isRequired,
  token: PropTypes.string,
};

export default gisReverseGeocodeLocation;
